import {Proposal} from "../../../types/Proposal";
import TelegramIcon from "@mui/icons-material/Telegram";
import {IconButton, Tooltip} from "@mui/material";
import React, {useMemo, useState} from "react";
import {useMutation} from "@tanstack/react-query";
import {DefaultError} from "@tanstack/query-core";
import {sendToTelegram} from "../../../service/proposal.service";
import {enqueueSnackbar} from "notistack";
import {SxProps} from "@mui/system";
import {useAuthStore} from "../../../store/auth.store";
import {TelegramChatModal} from "./TelegramChatModal";

export function TelegramButton({sx, proposal, callback}:{callback:() => void, sx?: SxProps, proposal: Proposal}) {
    const authStore = useAuthStore();
    const [showTelegramChatModal, setShowTelegramChatModal] = useState(false);

    const hasTelegramChatSettings = useMemo(() => {
        return !!authStore.user?.telegramChatId?.trim();
    }, [authStore.user?.telegramChatId]);

    const sendToTelegramMutation = useMutation<void, DefaultError, void>({
        mutationFn: () => sendToTelegram(proposal.id)
            .then(r => {
                enqueueSnackbar({variant: "success", message: "Sent"});
                callback();
                return r.data;
            }),
        onError: (error) => {
            enqueueSnackbar({message: "Failed to deliver message to Telegram chat: " + error.message, variant: 'error'});
        }
    });

    function performTelegramAction() {
        sendToTelegramMutation.mutate();
    }

    function handleClick() {
        if(!authStore.user) {
            enqueueSnackbar({message: "Current user is not loaded yet", variant: "error"});
            return;
        }
        if(hasTelegramChatSettings) {
            performTelegramAction();
            return;
        }
        setShowTelegramChatModal(true);
    }

    const title = !hasTelegramChatSettings
        ? "Configure your Telegram group, supergroup, or channel before sending"
        : "Send to Telegram";

    return <>
        <Tooltip sx={sx} title={title}>
            <span>
                <IconButton disabled={sendToTelegramMutation.isPending} onClick={handleClick}>
                    <TelegramIcon color={"primary"}/>
                </IconButton>
            </span>
        </Tooltip>
        {showTelegramChatModal && authStore.user && <TelegramChatModal
            user={authStore.user}
            onClose={() => setShowTelegramChatModal(false)}
            onSuccess={() => {
                setShowTelegramChatModal(false);
                performTelegramAction();
            }}
        />}
    </>;
}
